import { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useCurrentCategory } from './useCurrentCategory/useCurrentCategory';
import { usePageType } from './usePageType';
import { usePlatformSource } from './usePlatformSource';
import { getFlowId } from './useAdSubmissionFlow/useAdSubmissionFlow.helpers';
import { PageType } from './usePageType/usePageType.types';
import { GET_CATEGORY_PROPERTIES } from '@/api/queries/categories.queries';
import { GetCategoryPropertiesResponse } from '@/api/types/categories.types';
import {
  AdSubmissionStatus,
  CreateAdSubmissionResponse,
} from '@/api/types/ads.types';
import { CREATE_AD_SUBMISSION } from '@/api/mutations/ads.mutations';
import { ROUTES } from '@/constants/routes';
import { useAdSubmissionFlowStore } from '@/store/useAdSubmissionFlowStore';

export const useCategoryProperties = (
  categoryPath?: string,
  hasDefaultSubcategory?: boolean
) => {
  const { currentCategory } = useCurrentCategory();
  const pageType = usePageType();
  const source = usePlatformSource();
  const path = categoryPath || currentCategory?.path;

  const [sendCategorySelect] =
    useMutation<CreateAdSubmissionResponse>(CREATE_AD_SUBMISSION);

  const handleCategorySelectStatus = useCallback(
    (
      previousData: GetCategoryPropertiesResponse | undefined,
      categoryId: string
    ) => {
      const flowId = getFlowId();
      if (
        typeof window === 'undefined' ||
        pageType !== PageType.NewAd ||
        (!previousData && hasDefaultSubcategory) ||
        !flowId ||
        !categoryId ||
        path === ROUTES.ad.slice(1)
      ) {
        return;
      }
      sendCategorySelect({
        variables: {
          flowId,
          status: AdSubmissionStatus.CategorySelect,
          source,
          categoryId,
        },
      });
      useAdSubmissionFlowStore.getState().setCategoryId(categoryId);
    },
    [pageType, hasDefaultSubcategory, path, sendCategorySelect, source]
  );

  const {
    data,
    refetch: refetchCategoryProperties,
    loading,
    previousData,
  } = useQuery<GetCategoryPropertiesResponse>(GET_CATEGORY_PROPERTIES, {
    skip: !path || typeof window === 'undefined',
    variables: { path },
    errorPolicy: 'all',
    // Trigger CATEGORY_SELECT flow status once category data is resolved on new ad page
    onCompleted: ({ category }) => {
      const categoryId = category?.id;
      if (categoryId) {
        handleCategorySelectStatus(previousData, categoryId);
      }
    },
    onError: () => {
      const categoryId = data?.category?.id;
      if (categoryId) {
        handleCategorySelectStatus(previousData, categoryId);
      }
    },
  });

  const { id: categoryId, properties: categoryProperties } =
    data?.category ?? {};

  return {
    categoryId,
    categoryProperties,
    refetchCategoryProperties,
    isCategoriesLoading: loading,
  };
};
